// src/lib/generatePlan.js
// Builds a tailored one-page PDF "growth + AI plan" from the qualifier answers.
// Pure client-side (jsPDF) — no backend, no API. Content is templated off the
// visitor's choices so it feels genuinely personalized.
import { jsPDF } from 'jspdf';

/* ---- content engine: map answers -> recommendations ---- */

const FOCUS_PLAYBOOK = {
  // Growth branch
  'Scale paid ads (Google / Meta / TikTok)': {
    headline: 'Scale paid acquisition without blowing up CPA',
    channels: ['Google Search + PMax', 'Meta Advantage+', 'TikTok Spark Ads'],
    moves: [
      'Rebuild tracking on server-side CAPI so the algorithms optimize on clean data.',
      'Consolidate ad accounts and let the platforms find volume before layering controls.',
      'Ship a rapid creative-testing engine — 8–12 new hooks/week, kill losers fast.',
    ],
  },
  'Build a high-converting funnel': {
    headline: 'Turn traffic into booked revenue',
    channels: ['Landing pages', 'Quiz / qualification flow', 'Email + SMS nurture'],
    moves: [
      'Map the funnel end-to-end and find the single biggest drop-off to fix first.',
      'Build a qualification step so only high-intent leads reach your calendar.',
      'Add a nurture sequence for the 90% not ready to buy today.',
    ],
  },
  'Fix tracking & attribution': {
    headline: 'See what actually drives revenue',
    channels: ['GA4 + GTM', 'Meta CAPI', 'CRM attribution'],
    moves: [
      'Implement server-side tracking (CAPI) and advanced matching for signal loss.',
      'Wire closed-loop attribution from ad click → CRM → revenue.',
      'Stand up one dashboard leadership actually trusts.',
    ],
  },
  'Bring my marketing in-house': {
    headline: 'Own your marketing engine',
    channels: ['In-house team enablement', 'Process + SOPs', 'Tooling stack'],
    moves: [
      'Audit current agency spend and identify what to bring in-house first.',
      'Build the tracking + reporting infrastructure your team will own.',
      'Document repeatable playbooks so results don’t depend on any one person.',
    ],
  },
  // AI branch
  'AI voice agent / 24-7 receptionist': {
    headline: 'Never miss a lead again',
    channels: ['AI voice agent', 'Call routing', 'CRM sync'],
    moves: [
      'Deploy an AI voice agent that answers, qualifies, and books 24/7.',
      'Route qualified calls and log everything into your CRM automatically.',
      'Tune the qualification + objection-handling prompts on real transcripts.',
    ],
  },
  'CRM automation & lead follow-up': {
    headline: 'Automate the follow-up that closes deals',
    channels: ['GoHighLevel / HubSpot', 'Automated sequences', 'Lead scoring'],
    moves: [
      'Build pipelines, lead routing, and speed-to-lead automations.',
      'Add scoring so your team works the hottest leads first.',
      'Automate the repetitive follow-up that reps forget to send.',
    ],
  },
  'Data pipelines & reporting': {
    headline: 'One source of truth for the whole business',
    channels: ['Automated pipelines', 'Dashboards', 'Attribution'],
    moves: [
      'Pipe ad, CRM, and revenue data into one warehouse automatically.',
      'Kill manual reporting with live executive dashboards.',
      'Surface the metrics that actually change decisions.',
    ],
  },
  'Prompt engineering / custom AI': {
    headline: 'Custom AI wired into your business',
    channels: ['Custom AI workflows', 'MCP integrations', 'Prompt systems'],
    moves: [
      'Identify the highest-leverage manual task to automate with AI first.',
      'Build and evaluate production-grade prompts (not vibes).',
      'Integrate via MCP / APIs so AI plugs into your real tools.',
    ],
  },
  // Product branch
  'An AI-powered SaaS product': {
    headline: 'Ship your AI SaaS end-to-end',
    channels: ['Architecture', 'Full-stack build', 'Go-to-market'],
    moves: [
      'Scope an MVP that proves the core value in weeks, not months.',
      'Build the full stack (React, Node, Supabase) + AI layer.',
      'Launch with a go-to-market plan, not just a product.',
    ],
  },
  'A mobile app (iOS / Android)': {
    headline: 'From idea to app store',
    channels: ['Expo / React Native', 'Backend', 'Launch'],
    moves: [
      'Define the core loop and cut everything else from v1.',
      'Build cross-platform with Expo + a real backend.',
      'Plan the launch + retention mechanics before you ship.',
    ],
  },
  'An internal tool / automation': {
    headline: 'Automate the work eating your team’s time',
    channels: ['Custom tooling', 'Automation', 'Integrations'],
    moves: [
      'Map the manual process and quantify the hours it costs.',
      'Build a tool/automation that removes the busywork.',
      'Integrate with the systems your team already uses.',
    ],
  },
  'An MVP from scratch': {
    headline: 'Get to a working MVP fast',
    channels: ['Architecture', 'Full-stack build', 'Validation'],
    moves: [
      'Define the one thing the MVP must prove.',
      'Build lean across the full stack.',
      'Get it in front of real users and iterate.',
    ],
  },
  // Not-sure branch
  'More qualified leads': {
    headline: 'Fill the pipeline with the right leads',
    channels: ['Paid media', 'Qualification', 'Nurture'],
    moves: [
      'Dial in targeting + creative so you attract fit prospects.',
      'Add a qualification layer to filter out tire-kickers.',
      'Nurture the rest until they’re ready.',
    ],
  },
  'Lower cost per acquisition': {
    headline: 'Cut CPA without cutting volume',
    channels: ['Tracking', 'Creative testing', 'Funnel CRO'],
    moves: [
      'Fix tracking so platforms optimize on accurate conversions.',
      'Systematically test creative to lower cost-per-result.',
      'Improve funnel conversion so every click works harder.',
    ],
  },
  'Automate manual operations': {
    headline: 'Automate the ops slowing you down',
    channels: ['CRM automation', 'AI workflows', 'Integrations'],
    moves: [
      'Find the top 3 manual tasks costing the most time.',
      'Automate them with CRM workflows + AI.',
      'Connect your tools so data flows without copy-paste.',
    ],
  },
  'Launch something new': {
    headline: 'Launch with momentum',
    channels: ['Positioning', 'Funnel', 'Paid + organic'],
    moves: [
      'Nail positioning and the offer before spending a dollar.',
      'Build the launch funnel end-to-end.',
      'Drive traffic with a paid + organic launch plan.',
    ],
  },
};

const BUDGET_NOTE = {
  'Under $5k': 'Lean, high-leverage plays first — prove ROI before scaling spend.',
  '$5k – $25k': 'Enough to run real tests across 1–2 channels and build momentum.',
  '$25k – $100k': 'Room to scale aggressively across multiple channels in parallel.',
  '$100k+': 'Full multi-channel + AI infrastructure build, optimized for scale.',
};

const TIMELINE_NOTE = {
  'ASAP / this month': 'We can move immediately — priority is quick wins in week one.',
  'Next 1–3 months': 'Ideal runway to build foundations and launch properly.',
  'Just exploring for now': 'No pressure — this plan is a roadmap for when you’re ready.',
};

function fallbackFocus(intent) {
  return {
    headline: intent || 'Your custom growth + AI plan',
    channels: ['Strategy', 'Execution', 'Measurement'],
    moves: [
      'Start with a discovery call to pin down the highest-leverage move.',
      'Build the right foundation before scaling.',
      'Measure everything and double down on what works.',
    ],
  };
}

/* ---- PDF renderer ---- */

const INK = [15, 15, 25];
const MUTED = [110, 110, 130];
const ACCENT = [124, 58, 237]; // violet
const PINK = [236, 72, 153];

export function generatePlanPdf({ contact, answers }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 48; // margin
  let y = 0;

  const play = FOCUS_PLAYBOOK[answers.focus] || fallbackFocus(answers.intent);

  // Header band
  doc.setFillColor(...INK);
  doc.rect(0, 0, W, 132, 'F');
  // gradient-ish accent bar
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, W, 6, 'F');
  doc.setFillColor(...PINK);
  doc.rect(W / 2, 0, W / 2, 6, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Your Growth + AI Plan', M, 60);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(200, 200, 215);
  const preparedFor = contact.company
    ? `Prepared for ${contact.company}${contact.name ? ` · ${contact.name}` : ''}`
    : `Prepared for ${contact.name || 'you'}`;
  doc.text(preparedFor, M, 84);
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 170);
  doc.text('By Isabella Ibeji — Growth & Marketing Leader · AI Product Builder', M, 102);

  y = 172;

  // Snapshot row
  doc.setTextColor(...MUTED);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('YOUR SNAPSHOT', M, y);
  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...INK);
  const rows = [
    ['Looking for', answers.intent],
    ['Priority', answers.focus],
    ['Budget', answers.scale],
    ['Timeline', answers.timeline],
  ].filter(([, v]) => v);
  rows.forEach(([k, v]) => {
    doc.setTextColor(...MUTED);
    doc.text(k, M, y);
    doc.setTextColor(...INK);
    doc.text(String(v), M + 110, y);
    y += 18;
  });

  y += 14;
  divider(doc, M, y, W - M);
  y += 26;

  // Recommended approach
  sectionTitle(doc, 'RECOMMENDED APPROACH', M, y);
  y += 22;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...ACCENT);
  y = wrap(doc, play.headline, M, y, W - M * 2, 17);
  y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...INK);
  doc.text('Channels / systems to prioritize:', M, y);
  y += 16;
  doc.setTextColor(...ACCENT);
  doc.setFont('helvetica', 'bold');
  doc.text(play.channels.join('  ·  '), M, y);
  y += 26;

  // 3 moves
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...INK);
  doc.text('First moves:', M, y);
  y += 18;
  doc.setFont('helvetica', 'normal');
  play.moves.forEach((m, i) => {
    doc.setTextColor(...PINK);
    doc.setFont('helvetica', 'bold');
    doc.text(`${i + 1}.`, M, y);
    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'normal');
    y = wrap(doc, m, M + 18, y, W - M * 2 - 18, 15);
    y += 8;
  });

  y += 8;
  divider(doc, M, y, W - M);
  y += 26;

  // Budget + timeline notes
  sectionTitle(doc, 'FIT NOTES', M, y);
  y += 20;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...INK);
  if (answers.scale && BUDGET_NOTE[answers.scale]) {
    y = wrap(doc, `Budget — ${BUDGET_NOTE[answers.scale]}`, M, y, W - M * 2, 15);
    y += 6;
  }
  if (answers.timeline && TIMELINE_NOTE[answers.timeline]) {
    y = wrap(doc, `Timeline — ${TIMELINE_NOTE[answers.timeline]}`, M, y, W - M * 2, 15);
    y += 6;
  }

  // Footer CTA
  const fy = doc.internal.pageSize.getHeight() - 96;
  doc.setFillColor(...INK);
  doc.rect(0, fy, W, 96, 'F');
  doc.setFillColor(...ACCENT);
  doc.rect(0, fy, W, 4, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Next step: let’s build it.', M, fy + 38);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(200, 200, 215);
  doc.text('Book a free strategy call and we’ll turn this plan into an execution roadmap.', M, fy + 58);
  doc.setTextColor(180, 170, 255);
  doc.text('calendar.app.google/JNzzrs67cJGNb3Vq8', M, fy + 76);

  return doc;
}

/* ---- helpers ---- */

function divider(doc, x1, y, x2) {
  doc.setDrawColor(225, 225, 232);
  doc.setLineWidth(1);
  doc.line(x1, y, x2, y);
}

function sectionTitle(doc, label, x, y) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(label, x, y);
}

// wraps text, returns the new y
function wrap(doc, text, x, y, maxWidth, lineHeight) {
  const lines = doc.splitTextToSize(text, maxWidth);
  lines.forEach((ln, i) => doc.text(ln, x, y + i * lineHeight));
  return y + lines.length * lineHeight;
}

export function downloadPlan({ contact, answers }) {
  const doc = generatePlanPdf({ contact, answers });
  const safeCo = (contact.company || contact.name || 'plan')
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase();
  doc.save(`growth-ai-plan-${safeCo}.pdf`);
}
